import React from 'react';

/**
 * Pie chart skeleton — segmented circle with 5 slices and visible gaps.
 * Each slice pops out one at a time in a pseudo-random order.
 *
 * Animation cycle: 7s total (1.4s per slice × 5 slices).
 * Each slice is active for ~20% of the cycle, idle for 80%.
 * Small angular gaps between slices create a segmented look.
 */

interface SliceDef {
  startDeg: number;
  endDeg: number;
  fill: string;
  /** Mid-angle for outward motion direction. */
  breatheDeg: number;
  /** Staggered delay — pseudo-random order. */
  delay: string;
}

const SLICES: SliceDef[] = [
  { startDeg: 0,   endDeg: 108,  fill: '#E0E0E0', breatheDeg: 54,   delay: '1.4s'  },
  { startDeg: 108, endDeg: 198,  fill: '#EAEAEA', breatheDeg: 153,  delay: '4.2s'  },
  { startDeg: 198, endDeg: 270,  fill: '#D8D8D8', breatheDeg: 234,  delay: '0s'    },
  { startDeg: 270, endDeg: 324,  fill: '#E4E4E4', breatheDeg: 297,  delay: '2.8s'  },
  { startDeg: 324, endDeg: 360,  fill: '#DDDDDD', breatheDeg: 342,  delay: '5.6s'  },
];

const CENTER = 60;
const RADIUS = 46;
/** Gap between slices in degrees. */
const GAP_DEG = 2;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function slicePath(startDeg: number, endDeg: number): string {
  // Inset by GAP_DEG/2 on each side so slices don't touch
  const adjustedStart = startDeg + GAP_DEG / 2;
  const adjustedEnd = endDeg - GAP_DEG / 2;

  if (adjustedEnd <= adjustedStart) return '';

  const start = toRad(adjustedStart - 90);
  const end = toRad(adjustedEnd - 90);
  const largeArc = adjustedEnd - adjustedStart > 180 ? 1 : 0;

  const x1 = CENTER + RADIUS * Math.cos(start);
  const y1 = CENTER + RADIUS * Math.sin(start);
  const x2 = CENTER + RADIUS * Math.cos(end);
  const y2 = CENTER + RADIUS * Math.sin(end);

  return `M${CENTER},${CENTER} L${x1.toFixed(2)},${y1.toFixed(2)} A${RADIUS},${RADIUS} 0 ${largeArc} 1 ${x2.toFixed(2)},${y2.toFixed(2)} Z`;
}

/**
 * CSS translate values for outward motion along slice center angle.
 */
function getSliceMotion(angleDeg: number): { tx: string; ty: string } {
  const rad = toRad(angleDeg - 90);
  const dist = 5;
  return {
    tx: `${(Math.cos(rad) * dist).toFixed(2)}px`,
    ty: `${(Math.sin(rad) * dist).toFixed(2)}px`,
  };
}

interface PieChartSkeletonProps {
  /** Real chart title from plan preview — replaces shimmer placeholder. */
  title?: string;
}

const PieChartSkeleton: React.FC<PieChartSkeletonProps> = ({ title }) => (
  <div className="skeleton-chart-container">
    <div className="pie-skeleton">
      {title ? (
        <div className="chart-skeleton__real-title">{title}</div>
      ) : (
        <div className="skeleton-block pie-skeleton__title" />
      )}

      <div className="pie-skeleton__chart">
        <svg className="pie-skeleton__svg" viewBox="0 0 120 120">
          {SLICES.map((slice, i) => {
            const motion = getSliceMotion(slice.breatheDeg);
            const d = slicePath(slice.startDeg, slice.endDeg);
            if (!d) return null;
            return (
              <path
                key={i}
                className="pie-skeleton__slice"
                d={d}
                fill={slice.fill}
                style={{
                  '--slice-delay': slice.delay,
                  '--slice-tx': motion.tx,
                  '--slice-ty': motion.ty,
                  transformOrigin: `${CENTER}px ${CENTER}px`,
                } as React.CSSProperties}
              />
            );
          })}
        </svg>
      </div>
    </div>
  </div>
);

export default React.memo(PieChartSkeleton);